import dayjs, { Dayjs } from "dayjs";

import { useEffect, useRef, useState } from "react";
import { json, useSearchParams } from "react-router-dom";

import { REGION_LIST } from "@/constants/airport";
import { AIRPORT_MAP } from "@/constants/airport";
import { ROUTES_PAGE_INIT_LOADDATA } from "@/constants/eventName";
import { DEFAULT_PANORAMA_SEARCH_DAYS, MEMBERSHIP_CONFIG } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { dispatchEvent } from "@/hooks/useEventEmitter";
import { LocationOption } from "@/pages/Routes/hooks/useLocationFilter";
import { createTimeRange } from "@/utils/timeRange";

import { LocationEnum } from "./useLocationFilter";

export interface ConditionType {
  fromLocation: LocationOption[];
  toLocation: LocationOption[];
  startDate: number;
  endDate: number;
  pax: number;
  children: number;
  cabins: string;
  searchMode: string;
}

export function parseSearchQuery(searchParams: URLSearchParams) {
  const params = {} as any;

  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }
  const isExpires = Number(params.startDate) * 1000 - Date.now() <= 0;
  return {
    fromLocation: params.from
      ? params.from.split("-").map((ele: string) => {
          const [code, type] = ele.split("_");

          if (code === REGION_LIST[0].code) {
            return { ...REGION_LIST[0], code, type };
          }

          return { code: code, type: type };
        })
      : [],
    toLocation: params.to
      ? params.to.split("-").map((ele: string) => {
          const [code, type] = ele.split("_");

          if (code === REGION_LIST[0].code) {
            return { ...REGION_LIST[0], code, type };
          }

          return { code: code, type: type };
        })
      : [],
    startDate: params.startDate
      ? Math.max(
          Number(params.startDate) * 1000,
          dayjs(dayjs().format("MM/DD/YYYY")).unix() * 1000 + 3600 * 6 * 24 * 1000
        )
      : 0,
    endDate: params.endDate
      ? isExpires
        ? dayjs(dayjs().format("MM/DD/YYYY")).unix() * 1000 + 3600 * 13 * 24 * 1000
        : Number(params.endDate) * 1000
      : 0,
    pax: params.pax || 1,
    children: params.children || 0,
    cabins: params.cabins,
  };
}

export function createSearchQuery(condition: ConditionType) {
  let date = {};

  date = {
    startDate: condition.startDate / 1000,
    endDate: condition.endDate / 1000,
  };

  return {
    ...condition,
    from: condition.fromLocation.map((ele) => `${ele.code}_${ele.type}`).join("-"),
    to: condition.toLocation.map((ele) => `${ele.code}_${ele.type}`).join("-"),
    ...date,
  };
}

const useCondition = (useWhere?: "home" | "routes") => {
  const [searchParams] = useSearchParams();
  const isRoutesFlights = window.location.pathname === "/routes-flights";
  const initParams = useRef({
    ...parseSearchQuery(searchParams),
    searchMode: isRoutesFlights
      ? "true"
      : localStorage.getItem("show_individual_flights") ?? "false",
  });

  const [condition, setCondition] = useState<ConditionType>(initParams.current);
  const store = useGlobalStoreContext();

  const route_range = DEFAULT_PANORAMA_SEARCH_DAYS;
  useEffect(() => {
    if (!store.load) return;

    let data = initParams.current;

    // 是否被使用在首页
    const localData = localStorage.getItem("routes_search_conditions");
    // 本地存在优先
    if (localData) {
      if (initParams.current.fromLocation.length > 0) {
        data = {
          ...JSON.parse(localData),
          ...initParams.current,
        };
      } else {
        data = JSON.parse(localData);
      }
      // 过期检测重置
      if (dayjs(data.startDate).diff(dayjs(), "D") < 0) {
        const { startDate, endDate } = createTimeRange(7, route_range - 1);

        Object.assign(data, {
          startDate: startDate.valueOf(),
          endDate: endDate.valueOf(),
          pax: data.pax || 1,
          children: data.children || 0,
        });
      }
    } else {
      const { startDate, endDate } = createTimeRange(7, route_range - 1);

      let fromLocation = [{ ...REGION_LIST[0], type: LocationEnum.REGION }];

      if (store?.closest_airport) {
        const country = AIRPORT_MAP[store?.closest_airport]["country"];

        if (country) {
          fromLocation = [
            {
              type: LocationEnum.AIRPORT,
              code: store?.closest_airport,
              name: "United States",
            },
          ];
          // const path = window.location.pathname;

          // if (path === "/") {
          //   fromLocation = [
          //     { type: LocationEnum.AIRPORT, code: store?.closest_airport, name: "United States" },
          //   ];
          // } else {
          //   fromLocation = [{ type: LocationEnum.COUNTRY, code: country, name: "United States" }];
          // }
        }
      }

      data = {
        startDate: dayjs(startDate).valueOf(),
        endDate: dayjs(endDate).valueOf(),
        fromLocation,
        toLocation: [
          { ...REGION_LIST[3], type: LocationEnum.REGION },
          { ...REGION_LIST[2], type: LocationEnum.REGION },
        ],
        pax: data.pax || 1,
        children: data.children || 0,
      };
    }

    if (!data.cabins) {
      if (store?.user_preference?.cabins) {
        Object.assign(data, { cabins: store.user_preference.cabins });
      } else {
        Object.assign(data, { cabins: "Economy&Premium Economy&Business&First" });
      }
    }

    setCondition({ ...data, searchMode: initParams.current.searchMode });

    dispatchEvent(ROUTES_PAGE_INIT_LOADDATA, data);
  }, [route_range, store.load, store?.closest_airport, store?.user_preference?.cabins, useWhere]);

  const onDateChange = useEvent((date: Array<Dayjs | null>) => {
    setCondition((pre) => {
      return {
        ...pre,
        startDate: date[0] ? dayjs(date[0]).valueOf() : 0,
        endDate: date[1] ? dayjs(date[1]).valueOf() : 0,
      };
    });
  });

  const onLocationSwapChange = useEvent(() => {
    setCondition((pre) => {
      return {
        ...pre,
        fromLocation: pre.toLocation,
        toLocation: pre.fromLocation,
      };
    });
  });

  return {
    condition,
    setCondition,
    onLocationSwapChange,
    onDateChange,
  };
};

export default useCondition;
